import { Checkbox, FormControlLabel, Grid, TextField, Typography, Button, CircularProgress, Snackbar, Alert, Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { fetchGracenoteSports, fetchGracenoteSportsPreferences, useGracenoteBulkSportsDeltaMutation } from 'core/API/gameMasterSports';
import { callActivateDeactivateScheduling, DEACTIVATE_SCHEDULING_CONFIRM_MESSAGE, getSchedulingShouldActivate } from 'core/API/gameMasterScheduling';
import Cookies from 'js-cookie';
import React, { useMemo, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Sport, GracenoteSportPreference } from 'types';
import styles from './GamemasterSportSelection.module.scss'; // Adjust path if needed
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { Edit } from '@mui/icons-material';

interface GamemasterSportSelectionProps {
    onViewModeChange?: (mode: 'list' | 'edit') => void;
    canEdit?: boolean;
}

const GamemasterSportSelection: React.FC<GamemasterSportSelectionProps> = ({ onViewModeChange, canEdit = false }) => {
    const { workplaceId } = useWorkplacesContext();
    const [isExpanded, setIsExpanded] = useState(true);
    const [viewMode, setViewMode] = useState<'list' | 'edit'>('list');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSports, setSelectedSports] = useState<string[]>([]);
    const [initialSelectedSports, setInitialSelectedSports] = useState<string[]>([]);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmNoSportsDialogOpen, setConfirmNoSportsDialogOpen] = useState(false);
    const [confirmDeactivateAfterValidationOpen, setConfirmDeactivateAfterValidationOpen] = useState(false);
    const [pendingDeactivateWorkplaceId, setPendingDeactivateWorkplaceId] = useState<number | null>(null);

    const queryClient = useQueryClient();
    const recommendedSportNames = useMemo(() => ([
        'Football',
        'Basketball',
        'Hockey',
        'Baseball',
        'Golf',
        'Summer Olympics',
        'Winter Olympics',
        'Olympics',
        'Auto Racing',
        'Alpine Skiing',
        'Arm Wrestling',
        'Badminton',
        'Bicycle Racing',
        'Bobsled',
        'BMX Racing',
        'Bowling',
        'Cornhole',
        'Cross- country skiing',
        'Curling',
        'Diving',
        'Darts',
        'Dog sled',
        'Fencing',
        'Field Hockey',
        'Figure Skating',
        'Fishing',
        'Freestyle Skiing',
        'Gymnastics',
        'Horse Racing',
        'Harness Racing',
        'Marathon',
        'Motorsports',
        'Mountain Biking',
        'Pickle Ball',
        'Polo',
        'Road Cycling',
        'Rugby',
        'Rowing',
        'Rodeo',
        'Running',
        'Sailing',
        'Skateboarding',
        'Skating',
        'Ski Jumping',
        'Snowboarding',
        'Soccer',
        'Softball',
        'Squash',
        'Speed Skating',
        'Sport Climbing',
        'Summer Paralympics',
        'Swimming',
        'Tennis',
        'Table Tennis',
        'Track/Field',
        'Track Cycling',
        'Triathalon',
        'Triathlon',
        'Volleyball',
        'Water Skiing',
        'Winter Paralympics',
        'Yacht Racing',
        'Weightlifting',
    ]), []);

    const normalizeSportName = (name: string) => (
        name
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '')
    );

    const recommendedSportKeys = useMemo(() => (
        new Set(recommendedSportNames.map((name) => normalizeSportName(name)))
    ), [recommendedSportNames]);

    // Fetch all sports using useQuery
    // Removed initialData so isLoading stays true until actual data arrives
    const {
        data: sports = [],
        isLoading: isSportsLoading,
        isFetching: isSportsFetching,
        error: sportsError,
    } = useQuery<Sport[], Error>('sports', fetchGracenoteSports);

    // Fetch pre-selected sports preferences using useQuery
    const {
        data: preferences = [],
        isLoading: isPreferencesLoading,
        error: preferencesError,
        refetch: refetchPreferences,
    } = useQuery<GracenoteSportPreference[], Error>(['sportsPreferences', workplaceId], () => fetchGracenoteSportsPreferences(workplaceId), {
        enabled: !!workplaceId,
        initialData: [],
        retry: false, // Don't retry if it fails
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 5 * 60 * 1000, // 5 minutes - prevent unnecessary refetches
    });

    // Combine loading and error states
    // Show loader if sports are loading/fetching OR preferences are loading
    const isLoading = isSportsLoading || isSportsFetching || isPreferencesLoading;
    const error = sportsError; // Only show error if main sports API fails, not preferences

    // Sync selectedSports with preferences
    // Match by: sportId == gracenote (from preferences)
    useEffect(() => {
        if (preferences && preferences.length > 0 && sports.length > 0) {
            // Get gracenote IDs from preferences
            const preferenceGracenoteIds = new Set(
                preferences
                    .filter((pref) => pref.sport && typeof pref.sport.gracenote !== 'undefined')
                    .map((pref) => pref.sport.gracenote)
            );
            
            // Find matching sports from the sports list
            // Match: sport.sportsId (as number) == pref.sport.gracenote
            const matchedSportsIds = sports
                .filter((sport) => {
                    // Convert sportsId to number and match with gracenote from preferences
                    const sportIdNum = Number(sport.sportsId);
                    return preferenceGracenoteIds.has(sportIdNum);
                })
                .map((sport) => String(sport.sportsId));
            
            setSelectedSports(matchedSportsIds);
            setInitialSelectedSports(matchedSportsIds);
        } else if (preferences && preferences.length === 0) {
            // Clear selection if no preferences
            setSelectedSports([]);
            setInitialSelectedSports([]);
        }
    }, [preferences, sports]);

    // Debug logging to verify data
    useEffect(() => {
        // console.log('Sports:', sports.map((s) => ({ id: s.sportsId, name: s.sportsName })));
        // console.log('Preferences (API):', preferences);
        // console.log('SelectedSports (state):', selectedSports);
        sports.forEach((sport) => {
            // console.log(`Sport ID: ${sport.sportsId}, Selected: ${selectedSports.includes(String(sport.sportsId))}`);
        });
    }, [sports, preferences, selectedSports]);

    // Mutation for saving sports preferences
    const { mutate: savePreferences, isLoading: isSaving } = useGracenoteBulkSportsDeltaMutation();

    // Handle save button click
    const handleSave = () => {
        if (!canEdit) {
            setSnackbar({
                open: true,
                message: 'You do not have permission to edit sports.',
                severity: 'error',
            });
            return;
        }
        // If no sports selected, show deactivate scheduling confirmation
        if (selectedSports.length === 0) {
            setConfirmNoSportsDialogOpen(true);
            return;
        }

        // If less than 6 sports selected, prevent save
        if (selectedSports.length < 6) {
            setSnackbar({
                open: true,
                message: 'To ensure a reasonable schedule you must select at least 6 sports.',
                severity: 'error',
            });
            return;
        }

        // If less than 10 sports selected, show confirmation dialog
        if (selectedSports.length < 10) {
            setConfirmDialogOpen(true);
            return;
        }

        // Clear validation error if sports are selected
        setValidationError(null);
        
        // Proceed with save
        performSave();
    };

    const handleConfirmNoSportsContinue = () => {
        setConfirmNoSportsDialogOpen(false);
        performSave();
    };

    const handleConfirmNoSportsCancel = () => {
        setConfirmNoSportsDialogOpen(false);
    };

    const handleConfirmDeactivateAfterValidation = async () => {
        if (pendingDeactivateWorkplaceId == null) {
            setConfirmDeactivateAfterValidationOpen(false);
            setPendingDeactivateWorkplaceId(null);
            return;
        }
        try {
            await callActivateDeactivateScheduling(pendingDeactivateWorkplaceId, 0);
            await queryClient.invalidateQueries(['gameMasterSettings', pendingDeactivateWorkplaceId]);
        } catch (e) {
            console.warn('Deactivate scheduling call failed:', e);
        }
        setConfirmDeactivateAfterValidationOpen(false);
        setPendingDeactivateWorkplaceId(null);
    };

    const handleCancelDeactivateAfterValidation = () => {
        setConfirmDeactivateAfterValidationOpen(false);
        setPendingDeactivateWorkplaceId(null);
    };

    // Perform the actual save operation
    const performSave = () => {
        const wid = workplaceId || Number(localStorage.getItem("workplace-id"));

        // Create maps of gracenote ID to list_order and maxNumberOfGames from existing preferences
        // This ensures we maintain the sports ranking priority
        const gracenoteToOrderMap: Record<number, number> = {};
        const gracenoteToMaxGamesMap: Record<number, number> = {};
        const maxOrder = preferences.length > 0 
            ? Math.max(...preferences.map(p => p.list_order || 0))
            : 0;
        
        preferences.forEach(pref => {
            if (pref.sport?.gracenote !== undefined) {
                gracenoteToOrderMap[pref.sport.gracenote] = pref.list_order || 0;
                gracenoteToMaxGamesMap[pref.sport.gracenote] = pref.max_number_of_games || 2;
            }
        });

        // Separate existing sports (with ranking) and new sports (without ranking)
        const existingSports: Array<{ gracenoteId: number; listOrder: number; maxGames: number }> = [];
        const newSports: number[] = [];
        
        selectedSports.forEach((sportIdStr) => {
            const gracenoteId = Number(sportIdStr);
            const existingOrder = gracenoteToOrderMap[gracenoteId];
            
            if (existingOrder !== undefined) {
                existingSports.push({ 
                    gracenoteId, 
                    listOrder: existingOrder,
                    maxGames: gracenoteToMaxGamesMap[gracenoteId] || 2
                });
            } else {
                newSports.push(gracenoteId);
            }
        });

        // Map selectedSports to payload format
        // POST API expects sport.id to be gracenote ID
        // Since sportsId == gracenote, we can use sportsId directly as gracenote ID
        const sportsPayload = [
            // Existing sports with their ranking order and max games
            ...existingSports.map(({ gracenoteId, listOrder, maxGames }) => ({
                sport: { id: gracenoteId },
                maxNumberOfGames: maxGames,
                listOrder: listOrder,
            })),
            // New sports assigned sequential orders after existing ones
            ...newSports.map((gracenoteId, index) => ({
                sport: { id: gracenoteId },
                maxNumberOfGames: 2,
                listOrder: maxOrder + 1 + index,
            }))
        ];

        // Sort by listOrder to maintain ranking priority order
        sportsPayload.sort((a, b) => a.listOrder - b.listOrder);

        // New payload shape for /game_master_preference_sports
        const payload = {
            workplace: { id: wid },
            sports: sportsPayload,
        };

        savePreferences(payload, {
            onSuccess: async () => {
                setSnackbar({
                    open: true,
                    message: 'Sports preferences saved successfully',
                    severity: 'success',
                });
                // Only invalidate - useQuery will handle refetch automatically
                await queryClient.invalidateQueries(['sportsPreferences', workplaceId]);
                // Update initial state to match current state after save
                setInitialSelectedSports([...selectedSports]);
                // Switch back to list view after successful save
                setViewMode('list');
                onViewModeChange?.('list');
                // Close confirmation dialog if open
                setConfirmDialogOpen(false);
                setConfirmNoSportsDialogOpen(false);
                const workplaceIdValue = workplaceId || Number(localStorage.getItem('workplace-id'));
                if (workplaceIdValue) {
                    try {
                        if (selectedSports.length === 0) {
                            await callActivateDeactivateScheduling(workplaceIdValue, 0);
                            await queryClient.invalidateQueries(['gameMasterSettings', workplaceIdValue]);
                        } else {
                            const shouldActivate = await getSchedulingShouldActivate(workplaceIdValue);
                            if (shouldActivate) {
                                await callActivateDeactivateScheduling(workplaceIdValue, 1);
                                await queryClient.invalidateQueries(['gameMasterSettings', workplaceIdValue]);
                            } else {
                                setPendingDeactivateWorkplaceId(workplaceIdValue);
                                setConfirmDeactivateAfterValidationOpen(true);
                            }
                        }
                    } catch (e) {
                        console.warn('Scheduling API call failed:', e);
                    }
                }
            },
            onError: (err) => {
                setSnackbar({
                    open: true,
                    message: 'Failed to save sports preferences. Please try again.',
                    severity: 'error',
                });
                console.error('Save Error:', err);
            },
        });
    };

    // Handle confirmation dialog continue
    const handleConfirmContinue = () => {
        setConfirmDialogOpen(false);
        performSave();
    };

    // Handle confirmation dialog cancel
    const handleConfirmCancel = () => {
        setConfirmDialogOpen(false);
    };

    // Handle edit button click
    const handleEditClick = () => {
        if (!canEdit) {
            return;
        }
        // Store initial state when entering edit mode
        setInitialSelectedSports([...selectedSports]);
        setViewMode('edit');
        onViewModeChange?.('edit');
    };

    // Handle back button click
    const handleBackClick = () => {
        // Reset selectedSports to initial state when going back without saving
        setSelectedSports([...initialSelectedSports]);
        setViewMode('list');
        setSearchTerm('');
        setValidationError(null);
        onViewModeChange?.('list');
    };

    // Check if changes were made
    const hasChanges = useMemo(() => {
        if (selectedSports.length !== initialSelectedSports.length) {
            return true;
        }
        const selectedSet = new Set(selectedSports);
        const initialSet = new Set(initialSelectedSports);
        return selectedSports.some(id => !initialSet.has(id)) || 
               initialSelectedSports.some(id => !selectedSet.has(id));
    }, [selectedSports, initialSelectedSports]);

    // Notify parent of initial viewMode
    useEffect(() => {
        onViewModeChange?.('list');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Get selected sports data for display, sorted alphabetically
    const selectedSportsData = useMemo(() => {
        // Filter selected sports
        const selectedSportsList = sports
            .filter((sport) => selectedSports.includes(String(sport.sportsId)));

        // Sort alphabetically by sport name
        return selectedSportsList.sort((a, b) => {
            const nameA = a.sportsName?.toLowerCase() || '';
            const nameB = b.sportsName?.toLowerCase() || '';
            return nameA.localeCompare(nameB);
        });
    }, [sports, selectedSports]);

    // Toggle accordion
    const toggleAccordion = () => {
        setIsExpanded(!isExpanded);
    };

    // Filter sports based on search term and sort alphabetically
    const filteredSports = useMemo(() => {
        return sports
            .filter((sport) =>
                sport.sportsName?.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .sort((a, b) => {
                const nameA = a.sportsName?.toLowerCase() || '';
                const nameB = b.sportsName?.toLowerCase() || '';
                return nameA.localeCompare(nameB);
            });
    }, [sports, searchTerm]);

    // Check if all filtered sports are selected
    const isAllSelected = useMemo(() => {
        return filteredSports.length > 0 && filteredSports.every((sport) =>
            selectedSports.includes(String(sport.sportsId))
        );
    }, [filteredSports, selectedSports]);

    const recommendedSportIds = useMemo(() => {
        if (!sports.length) {
            return [];
        }
        return sports
            .filter((sport) => recommendedSportKeys.has(normalizeSportName(sport.sportsName || '')))
            .map((sport) => String(sport.sportsId));
    }, [sports, recommendedSportKeys]);

    const isRecommendedSelected = useMemo(() => {
        if (recommendedSportIds.length === 0) {
            return false;
        }
        return recommendedSportIds.every((id) => selectedSports.includes(id));
    }, [recommendedSportIds, selectedSports]);

    // Handle "Select all" checkbox
    const handleSelectAll = () => {
        if (isAllSelected) {
            setSelectedSports(
                selectedSports.filter((id) => !filteredSports.some((sport) => String(sport.sportsId) === id))
            );
        } else {
            const newSelections = Array.from(
                new Set([...selectedSports, ...filteredSports.map((sport) => String(sport.sportsId))])
            );
            setSelectedSports(newSelections);
        }
        // Clear validation error when user makes changes
        setValidationError(null);
    };

    const handleSelectRecommended = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        if (checked) {
            setSelectedSports((prev) => Array.from(new Set([...prev, ...recommendedSportIds])));
        } else {
            setSelectedSports((prev) => prev.filter((id) => !recommendedSportIds.includes(id)));
        }
        setValidationError(null);
    };

    // Handle individual sport checkbox toggle
    const handleSportToggle = (sportId: string) => {
        setSelectedSports((prev) => {
            const newSelection = prev.includes(sportId) 
                ? prev.filter((id) => id !== sportId) 
                : [...prev, sportId];
            // Clear validation error when user makes changes
            setValidationError(null);
            return newSelection;
        });
    };




    // Error message for no sports selected
    const errorMessage = selectedSports.length === 0 ? 'Please select at least one sport.' : null;

    return (
        <div className={styles.container}>
            {/* Accordion Header */}
            <div className={styles.accordionHeader}>
                <button onClick={toggleAccordion} style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                    <h5>Sports Selection ({selectedSports.length})</h5>
                </button>
                {viewMode === 'list' && (
                    <button
                        className={styles.editButton}
                        onClick={handleEditClick}
                        title={canEdit ? "Edit Sports" : "You do not have permission to edit"}
                        style={{
                            marginLeft: 'auto',
                            opacity: canEdit ? 1 : 0.5,
                            cursor: canEdit ? 'pointer' : 'not-allowed',
                        }}
                        disabled={!canEdit}
                    >
                        <Edit className={styles.editIcon} />
                    </button>
                )}
            </div>

            {/* Accordion Content */}
            <div
                className={`${styles.accordionContent} ${isExpanded ? styles.expanded : ''}`}
                style={{ height: isExpanded ? 'max-content' : '0' }}
            >
                {viewMode === 'list' ? (
                    <>
                        <div className={styles.deviceCard}>
                            {/* Show loader while sports API is loading - initial case */}
                            {/* Show loader until sports data arrives */}
                            {isLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                                    <CircularProgress size={25} />
                                </Box>
                            ) : selectedSportsData.length > 0 ? (
                                <div className={styles.tableContainer}>
                                    <table className={styles.sportsTable}>
                                        <thead>
                                            <tr>
                                                <th>Sport Name</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedSportsData.map((sport) => (
                                                <tr key={sport.sportsId}>
                                                    <td>{sport.sportsName || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className={styles.noDataMessage}>No record found</div>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        {/* Edit Mode */}
                        <div className={styles.editSportSelectionSetting}>
                            <div className={styles.formContainer}>
                                <Grid container direction="column" spacing={2} pl={2}>
                                    <Grid container direction="row" justifyContent="space-between">
                                        <Grid>
                                            <Button className={styles.backButton} onClick={handleBackClick}>
                                                Back
                                            </Button>
                                        </Grid>
                                        <Grid item xs={6} display={'flex'} justifyContent="flex-end">
                                            <Grid>
                                                <Button
                                                    className={styles.saveButton}
                                                    onClick={handleSave}
                                                    disabled={!canEdit || isSaving || selectedSports.length === 0 || (selectedSports.length > 0 && selectedSports.length < 6) || !hasChanges}
                                                    title={selectedSports.length === 0 ? "Select at least one sport to save" : canEdit ? "Save" : "You do not have permission to edit"}
                                                >
                                                    {isSaving ? "Saving..." : 'Save'}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
                                        <h2 className={styles.formTitle}>Edit Sports</h2>
                                    </Grid>
                                    {/* Info message for less than 6 sports */}
                                    {selectedSports.length > 0 && selectedSports.length < 6 && (
                                        <Grid xs={12} style={{ marginTop: '8px', marginBottom: '8px' }}>
                                            <Alert severity="info" style={{ marginLeft: 14, marginRight: 14 }}>
                                                To ensure a reasonable schedule you must select at least 6 sports.
                                            </Alert>
                                        </Grid>
                                    )}
                                </Grid>

                                {/* Search Bar */}
                                <div className={styles.searchBar}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Search sports..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>

                                {/* Loading State */}
                                {isLoading && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                                        <CircularProgress size={25} />
                                    </Box>
                                )}

                                {/* Error State */}
                                {error && (
                                    <Typography className={styles.statusMessage} variant="body2" color="error">
                                        {error.message || 'Failed to load sports or preferences.'}
                                    </Typography>
                                )}

                                {!isLoading && !error && (
                                    <>
                                        {/* Select All Checkbox */}
                                        <div className={styles.selectAll}>
                                            <div className={styles.selectAllLeft}>
                                                <FormControlLabel
                                                    control={<Checkbox style={{
                                                        color: 'rgb(68, 99, 136)',
                                                    }} checked={isAllSelected} onChange={handleSelectAll} />}
                                                    label="Select All"
                                                />
                                            </div>
                                            <div className={styles.selectAllRight}>
                                                <FormControlLabel
                                                    control={<Checkbox
                                                        style={{ color: 'rgb(68, 99, 136)' }}
                                                        checked={isRecommendedSelected}
                                                        indeterminate={!isRecommendedSelected && recommendedSportIds.some((id) => selectedSports.includes(id))}
                                                        onChange={handleSelectRecommended}
                                                    />}
                                                    label="Select Recommended"
                                                />
                                            </div>
                                        </div>

                                        {/* Sports List */}
                                        {filteredSports.length === 0 ? (
                                            <Typography className={styles.noSportsText} variant="body2">
                                                No sports found for {searchTerm}.
                                            </Typography>
                                        ) : (
                                            <>
                                                <div className={styles.checkboxGrid}>
                                                    <Grid container spacing={1}>
                                                        {filteredSports.map((sport) => (
                                                            <Grid item xs={3} sm={3} md={3} lg={3} key={sport.sportsId}>
                                                                <div style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                }}>
                                                                    <Checkbox
                                                                        style={{
                                                                            color: 'rgb(68, 99, 136)',
                                                                        }}
                                                                        key={`sport-${sport.sportsId}`}
                                                                        checked={selectedSports.includes(String(sport.sportsId))}
                                                                        onChange={() => handleSportToggle(String(sport.sportsId))}
                                                                    />
                                                                    <h6> {sport.sportsName} </h6>
                                                                </div>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Confirmation Dialog for less than 10 sports - same style as delete screen group */}
            <Dialog
                open={confirmDialogOpen}
                onClose={handleConfirmCancel}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
                maxWidth="sm"
                fullWidth
                style={{ zIndex: 9999 }}
                PaperProps={{
                    style: {
                        backgroundColor: 'white',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        minWidth: '400px'
                    }
                }}
            >
                <DialogTitle id="confirm-dialog-title">
                    Are you Sure?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        GameMaster needs sufficient sports to ensure you have a great schedule. We recommend at least 10 sports be selected, especially sports like Football, Basketball, Baseball, and Hockey.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmCancel} style={{ color: 'black' }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmContinue} 
                        style={{ backgroundColor: '#4caf50', color: 'white' }}
                        autoFocus
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation dialog when saving with no sports (deactivate scheduling) */}
            <Dialog
                open={confirmNoSportsDialogOpen}
                onClose={handleConfirmNoSportsCancel}
                aria-labelledby="no-sports-dialog-title"
                aria-describedby="no-sports-dialog-description"
                maxWidth="sm"
                fullWidth
                style={{ zIndex: 9999 }}
                PaperProps={{
                    style: {
                        backgroundColor: 'white',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        minWidth: '400px'
                    }
                }}
            >
                <DialogTitle id="no-sports-dialog-title">Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText id="no-sports-dialog-description">
                        Saving with no sports selected will deactivate Game Master scheduling. {DEACTIVATE_SCHEDULING_CONFIRM_MESSAGE}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmNoSportsCancel} style={{ color: 'black' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmNoSportsContinue}
                        style={{ backgroundColor: 'red', color: 'white' }}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation dialog when validation fails (e.g. no provider/screen group) - deactivate with same message */}
            <Dialog
                open={confirmDeactivateAfterValidationOpen}
                onClose={handleCancelDeactivateAfterValidation}
                aria-labelledby="deactivate-validation-dialog-title"
                aria-describedby="deactivate-validation-dialog-description"
                maxWidth="sm"
                fullWidth
                style={{ zIndex: 9999 }}
                PaperProps={{
                    style: {
                        backgroundColor: 'white',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        minWidth: '400px'
                    }
                }}
            >
                <DialogTitle id="deactivate-validation-dialog-title">Confirm</DialogTitle>
                <DialogContent>
                    <DialogContentText id="deactivate-validation-dialog-description">
                        {DEACTIVATE_SCHEDULING_CONFIRM_MESSAGE}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDeactivateAfterValidation} style={{ color: 'black' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDeactivateAfterValidation}
                        style={{ backgroundColor: 'red', color: 'white' }}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default GamemasterSportSelection;